import { useState, useEffect } from 'react'
import './CanteenKiosk.css'
import { SchoolLogo } from '../components/SchoolLogo'

interface CanteenMenuItem {
  id: string
  date: string
  meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snack'
  menu_number?: number
  name: string
  description?: string
  can_vote?: boolean
}

// Функция для fetch с автоматическими повторными попытками и таймаутом
async function fetchWithRetry(
  url: string, 
  options: RequestInit = {}, 
  retries: number = 3, 
  timeout: number = 7000
): Promise<Response> {
  for (let i = 0; i < retries; i++) {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeout)
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      return response
    } catch (error: any) {
      console.warn(`Fetch attempt ${i + 1}/${retries} failed:`, error.message)
      
      // Если это последняя попытка - бросаем ошибку
      if (i === retries - 1) {
        throw error
      }
      
      // Ждем перед следующей попыткой (экспоненциальная задержка)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000))
    }
  }
  
  throw new Error('All retry attempts failed')
}

interface MenuData {
  menu: CanteenMenuItem[]
  days_limit: number
  date_range: {
    start: string
    end: string
  }
}

// Předchozí pracovní den pro hodnocení na kiosku (včerejší jídlo / pátek v pondělí)
function getPreviousSchoolDay(testDate?: string | null): string | null {
  const today = testDate ? new Date(testDate + 'T12:00:00') : new Date()
  const dayOfWeek = today.getDay() // 0=ne, 1=po, ..., 6=so
  
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return null
  }
  
  if (dayOfWeek === 1) {
    today.setDate(today.getDate() - 3)
  } else {
    today.setDate(today.getDate() - 1)
  }
  
  const y = today.getFullYear()
  const m = String(today.getMonth() + 1).padStart(2, '0')
  const d = String(today.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Pokud v DB chybí menu pro předchozí školní den (prázdniny), vezmeme poslední dostupný oběd ≤ tento den. */
function resolveLunchEvaluationDate(
  menu: CanteenMenuItem[],
  preferred: string | null
): string | null {
  if (!preferred) return null
  const lunchDates = [
    ...new Set(menu.filter((m) => m.meal_type === 'lunch').map((m) => m.date)),
  ].sort()
  if (lunchDates.includes(preferred)) return preferred
  let fallback: string | null = null
  for (const d of lunchDates) {
    if (d <= preferred) fallback = d
  }
  return fallback
}

export default function CanteenKiosk() {
  const [menuData, setMenuData] = useState<MenuData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Режим тестирования - можно указать тестовую дату через URL параметр ?test_date=2025-12-05
  const urlParams = new URLSearchParams(window.location.search)
  const testDate = urlParams.get('test_date')
  
  // Определяем дату для оценки (предыдущий рабочий день)
  const evaluationDate = getPreviousSchoolDay(testDate)
  const lunchEvaluationDate = menuData
    ? resolveLunchEvaluationDate(menuData.menu, evaluationDate)
    : evaluationDate
  const today = lunchEvaluationDate

  // Оценки для формы
  const [lunch1Rating, setLunch1Rating] = useState(0)
  const [lunch2Rating, setLunch2Rating] = useState(0)
  const [serviceRating, setServiceRating] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    loadMenu()
    
    // Автоматическое обновление страницы в 5:00 и 7:00 (для получения свежего кода)
    const timeouts: number[] = []
    
    const schedulePageReload = () => {
      const now = new Date()
      const reloadTimes = [
        { hours: 5, minutes: 0 }, // 5:00
        { hours: 7, minutes: 0 }  // 7:00
      ]
      
      reloadTimes.forEach(({ hours, minutes }) => {
        const reloadTime = new Date()
        reloadTime.setHours(hours, minutes, 0, 0)
        
        // Если уже прошло это время сегодня, планируем на завтра
        if (now >= reloadTime) {
          reloadTime.setDate(reloadTime.getDate() + 1)
        }
        
        const msUntilReload = reloadTime.getTime() - now.getTime()
        
        console.log(`🔄 Запланирована перезагрузка киоска на ${reloadTime.toLocaleString('cs-CZ')}`)
        
        const timeoutId = setTimeout(() => {
          console.log(`🔄 Автоматическая перезагрузка киоска в ${hours}:${minutes.toString().padStart(2, '0')}`)
          window.location.reload()
        }, msUntilReload)
        
        timeouts.push(timeoutId)
      })
    }
    
    schedulePageReload()
    
    // Cleanup функция
    return () => {
      timeouts.forEach(id => clearTimeout(id))
    }
  }, [])

  const loadMenu = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || window.location.origin + '/api'
      const response = await fetchWithRetry(`${apiUrl}/public-canteen/menu`)
      
      if (!response.ok) {
        let errorMessage = 'Nepodařilo se načíst menu'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (e) {
          errorMessage = `Chyba ${response.status}: ${response.statusText}`
        }
        throw new Error(errorMessage)
      }
      
      const data: MenuData = await response.json()
      
      if (!data) {
        throw new Error('Prázdná odpověď ze serveru')
      }
      
      if (!Array.isArray(data.menu)) {
        throw new Error('Neplatná data z serveru: menu není pole')
      }
      
      setMenuData(data)
    } catch (err: any) {
      console.error('Error loading menu:', err)
      // Silent fail - не показываем ошибку пользователю
      // setError(err.message || 'Nepodařilo se načíst menu')
    } finally {
      setLoading(false)
    }
  }

  // Фильтруем только обеды за сегодня (или тестовую дату)
  const todayLunches = menuData?.menu.filter(item => {
    return item.meal_type === 'lunch' && item.date === today
  }) || []

  const lunch1 = todayLunches.find(item => item.menu_number === 1)
  const lunch2 = todayLunches.find(item => item.menu_number === 2)

  // Функция для форматирования описания: показать все через запятую
  const formatDescription = (description: string): string => {
    if (!description) return ''
    return description.split(',').map(part => part.trim()).filter(part => part.length > 0).join(', ')
  }

  const handleSubmitReview = async () => {
    // Проверяем, что выбран хотя бы один обед и обслуживание
    if ((lunch1Rating === 0 && lunch2Rating === 0) || serviceRating === 0) {
      alert('Prosím, ohodnoťte alespoň jeden oběd a kvalitu obsluhy')
      return
    }

    setSubmitting(true)
    
    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || window.location.origin + '/api'
      
      // Отправляем оценки для каждого выбранного обеда
      const promises = []
      
      if (lunch1Rating > 0 && lunch1) {
        promises.push(
          fetchWithRetry(`${apiUrl}/public-canteen/vote`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              menu_id: lunch1.id,
              food_quality_rating: lunch1Rating,
              service_quality_rating: serviceRating,
              kiosk_mode: true // Режим сенсорной панели - без ограничений
            })
          })
        )
      }
      
      if (lunch2Rating > 0 && lunch2) {
        promises.push(
          fetchWithRetry(`${apiUrl}/public-canteen/vote`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              menu_id: lunch2.id,
              food_quality_rating: lunch2Rating,
              service_quality_rating: serviceRating,
              kiosk_mode: true // Режим сенсорной панели - без ограничений
            })
          })
        )
      }

      const responses = await Promise.all(promises)
      
      for (const response of responses) {
        if (!response.ok) {
          let errorMessage = 'Nepodařilo se odeslat recenzi'
          try {
            const contentType = response.headers.get('content-type')
            if (contentType && contentType.includes('application/json')) {
              const errorData = await response.json()
              errorMessage = errorData.error || errorData.details || errorData.message || errorMessage
            } else {
              const text = await response.text()
              errorMessage = text || `Chyba ${response.status}`
            }
          } catch (e) {
            console.error('Error parsing error response:', e)
            errorMessage = `Chyba ${response.status}: ${response.statusText}`
          }
          throw new Error(errorMessage)
        }
        
        // Проверяем успешный ответ
        try {
          const contentType = response.headers.get('content-type')
          if (contentType && contentType.includes('application/json')) {
            const result = await response.json()
            console.log('Vote saved successfully:', result)
          }
        } catch (e) {
          console.warn('Could not parse success response:', e)
        }
      }
      
    } catch (err: any) {
      // Silent fail - логируем ошибку, но НЕ показываем пользователю
      console.error('Error submitting review (silent):', err)
      // Продолжаем как будто всё успешно
    }
    
    // ВСЕГДА показываем success message (даже если запрос провалился)
    setSubmitted(true)
    
    // Сброс формы через 3 секунды для следующего пользователя
    setTimeout(() => {
      setLunch1Rating(0)
      setLunch2Rating(0)
      setServiceRating(0)
      setSubmitted(false)
      setSubmitting(false)
    }, 3000)
  }

  if (loading) {
    return (
      <div className="canteen-kiosk">
        <div className="kiosk-loading">Načítání...</div>
      </div>
    )
  }

  if (error && !menuData) {
    return (
      <div className="canteen-kiosk">
        <div className="kiosk-error">❌ {error}</div>
      </div>
    )
  }

  // Если выходной день (суббота или воскресенье) - показываем сообщение
  if (evaluationDate === null) {
    return (
      <div className="canteen-kiosk">
        <div className="kiosk-header">
          <div className="kiosk-logo">
            <SchoolLogo height="80px" />
          </div>
          <h1>🍽️ Hodnocení jídla</h1>
        </div>
        <div className="kiosk-empty">
          <p style={{ fontSize: '2rem', marginTop: '4rem' }}>🏖️</p>
          <p style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Dnes není hodnocení</p>
          <p style={{ fontSize: '1.2rem', color: '#666' }}>Víkend - užijte si volno! 😊</p>
        </div>
      </div>
    )
  }

  const todayFormatted = new Date(today + 'T00:00:00').toLocaleDateString('cs-CZ', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })

  if (todayLunches.length === 0) {
    return (
      <div className="canteen-kiosk">
        <div className="kiosk-header">
          <h1>🍽️ Hodnocení jídla</h1>
          <p className="kiosk-date">{todayFormatted}</p>
          {testDate && (
            <p className="kiosk-test-mode">🧪 Testovací režim: {testDate}</p>
          )}
        </div>
        <div className="kiosk-empty">
          <p>Dnes není k dispozici žádný oběd k hodnocení.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="canteen-kiosk">
      <div className="kiosk-header">
        <div className="kiosk-logo">
          <SchoolLogo height="80px" />
        </div>
        <h1>🍽️ Hodnocení včerejšího jídla</h1>
        <p className="kiosk-date">{todayFormatted}</p>
        {testDate && (
          <p className="kiosk-test-mode">🧪 Testovací režim: {testDate}</p>
        )}
      </div>

      {/* Инструкция по оценке */}
      <div className="kiosk-instruction">
        <h3>📋 Jak hodnotit:</h3>
        <p><strong>1.</strong> Vzpomeňte si na včerejší oběd a vyberte jeden z obědů (1. nebo 2. chod).</p>
        <p><strong>2.</strong> Ohodnoťte kvalitu jídla a obsluhy hvězdičkami (1 = špatné, 5 = výborné).</p>
      </div>

      {submitted ? (
        <div className="kiosk-success-message">
          <h2>✅ Děkujeme za hodnocení!</h2>
          <p>Vaše hodnocení bylo úspěšně odesláno.</p>
        </div>
      ) : (
        <div className="kiosk-form">
          {/* Первый ход */}
          {lunch1 && (
            <div className="kiosk-meal-section">
              <div className="kiosk-meal-header-section">
                <h2>1. chod</h2>
                <div className="kiosk-meal-description">
                  {formatDescription(lunch1.name)}
                </div>
              </div>
              <div className="kiosk-rating-section">
                <div className="kiosk-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      className={`kiosk-star ${lunch1Rating >= star ? 'active' : ''}`}
                      onClick={() => {
                        setLunch1Rating(star)
                        // Если выбран первый ход, сбрасываем второй
                        if (star > 0) {
                          setLunch2Rating(0)
                        }
                      }}
                    >
                      {lunch1Rating >= star ? '⭐' : '☆'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Второй ход */}
          {lunch2 && (
            <div className="kiosk-meal-section">
              <div className="kiosk-meal-header-section">
                <h2>2. chod</h2>
                <div className="kiosk-meal-description">
                  {formatDescription(lunch2.name)}
                </div>
              </div>
              <div className="kiosk-rating-section">
                <div className="kiosk-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      className={`kiosk-star ${lunch2Rating >= star ? 'active' : ''}`}
                      onClick={() => {
                        setLunch2Rating(star)
                        // Если выбран второй ход, сбрасываем первый
                        if (star > 0) {
                          setLunch1Rating(0)
                        }
                      }}
                    >
                      {lunch2Rating >= star ? '⭐' : '☆'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Качество обслуживания */}
          <div className="kiosk-service-section">
            <h2>👥 Kvalita obsluhy</h2>
            <div className="kiosk-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  className={`kiosk-star ${serviceRating >= star ? 'active' : ''}`}
                  onClick={() => setServiceRating(star)}
                >
                  {serviceRating >= star ? '⭐' : '☆'}
                </button>
              ))}
            </div>
          </div>

          {/* Кнопка отправки */}
          <div className="kiosk-submit-section">
            <button
              className="kiosk-submit-button"
              onClick={handleSubmitReview}
              disabled={(lunch1Rating === 0 && lunch2Rating === 0) || serviceRating === 0 || submitting}
            >
              {submitting ? 'Odesílání...' : 'Odeslat hodnocení'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
